import React, { useRef, useImperativeHandle, useMemo, useEffect, useState } from 'react';
import { useMemoizedFn, useDeepCompareEffect } from 'ahooks';
import ReportWallpaper from '../../../components/ReportWallpaper';
import Comment, { ICommentValue } from '../Comment';
import UpdatesOfWeek from '../UpdatesOfWeek';
import MemoPendingApproval, { IMemoPendingApprovalInstance } from '../MemoPendingApproval';
import Calendar, { ICalendarV3Instance } from '../Calendar';
import { getDPSReportDateTitle } from '../../../utils';
import { useAutoSaveParams } from '@gp/hooks/useAutoSaveParams';
import type { IBaseProps } from '@home/types/common';
import type {
  IDPSReportFilter,
  IDPSReportCalendarProjectSimpleVO,
  IDPSReportHelperVO,
  IDPSReportCalendarVO,
} from '@gp/types/dpsReport/common';
import type {
  IDPSReportModuleTitles,
  IDPSReportUpdatesOfTheWeekVO,
  IDPSReportMemoPendingApprovalVO,
} from '@gp/types/dpsReport/v3';
import type { IDPSReportFormValue, IDPSReportModuleInstance } from '../../types';

export type IReportFormInstance = IDPSReportModuleInstance;

interface IReportFormProps extends IBaseProps {
  // 是否loading
  isLoading?: boolean;
  isCalendarInnerLoading?: boolean;
  setIsCalendarInnerLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  reportHelper?: IDPSReportHelperVO | null;
  filter: IDPSReportFilter;
  projects?: IDPSReportCalendarProjectSimpleVO[] | null;
  titles: IDPSReportModuleTitles;
  onTitleChange: (key: string, payload: string) => void;
  // calendar模块点击Refresh Data按钮的回调
  onCalendarRefreshData?: () => void;
  value: IDPSReportFormValue;
  onChange?: (payload: IDPSReportFormValue) => void;
}

const ReportForm: React.ForwardRefRenderFunction<IReportFormInstance, IReportFormProps> = (
  props,
  ref,
) => {
  const {
    isLoading,
    isCalendarInnerLoading,
    setIsCalendarInnerLoading,
    reportHelper,
    filter,
    projects,
    titles,
    onTitleChange,
    onCalendarRefreshData,
    value,
    onChange,
    initialParams,
    pageKeyCur,
    reportParams,
  } = props;

  const [isChangeValue, setIsChangeValue] = useState<boolean>(false);
  const hasChangedRef = useRef(false);

  const { comment, project_comment: projectComment, changes, memo, calendar } = value || {};
  const commentValue = { comment, commentsUpdates: projectComment };
  const dateTitle = getDPSReportDateTitle(reportHelper);

  const memoPendingApprovalInstance = useRef<IMemoPendingApprovalInstance | null>(null);
  const calendarInstance = useRef<ICalendarV3Instance | null>(null);

  useDeepCompareEffect(() => {
    if (reportParams) {
      calendarInstance.current?.initialize(reportParams);
      memoPendingApprovalInstance.current?.initialize(reportParams);
    }
  }, [reportParams]);

  const handleCommentChange = useMemoizedFn((payload: ICommentValue) => {
    if (payload?.comment !== '' && hasChangedRef.current) {
      setIsChangeValue(true);
    }
    hasChangedRef.current = true;
    const { comment, commentsUpdates } = payload || {};
    const valueTmp = { ...value, comment, project_comment: commentsUpdates };
    onChange?.(valueTmp);
  });

  const handleCommentTitleChange = (payload: string) => {
    setIsChangeValue(true);
    onTitleChange('comment_title', payload);
  };

  const handleUpdatesOfWeekChange = (payload: IDPSReportUpdatesOfTheWeekVO[]) => {
    setIsChangeValue(true);
    const valueTmp = { ...value, changes: payload || [] };
    onChange?.(valueTmp);
  };

  const handleUpdatesOfWeekTitleChange = (payload: string) => {
    setIsChangeValue(true);
    onTitleChange('changes_title', payload);
  };

  const handleMemoChange = (payload: IDPSReportMemoPendingApprovalVO[]) => {
    setIsChangeValue(true);
    const valueTmp = { ...value, memo: payload || [] };
    onChange?.(valueTmp);
  };

  const handleMemoTitleChange = (payload: string) => {
    onTitleChange('memo_title', payload);
  };

  const handleCalendarChange = (payload: {
    start_date: string;
    end_date: string;
    list: IDPSReportCalendarVO[];
  }) => {
    setIsChangeValue(true);
    const valueTmp = { ...value, calendar: payload };
    onChange?.(valueTmp);
  };

  const handleCalendarTitleChange = (payload: string) => {
    onTitleChange('calendar_title', payload);
  };

  useImperativeHandle(ref, () => ({
    initialize: (payload) => {
      calendarInstance.current?.initialize(payload);
      memoPendingApprovalInstance.current?.initialize(payload);
    },
  }));

  const saveParamsCur = useMemo(() => {
    return {
      title: titles,
      report: value,
    };
  }, [titles, value]);

  // 每隔5秒自动保存
  useAutoSaveParams({
    pageKey: pageKeyCur,
    initialParams,
    saveParamsCur,
    isBegin: true,
    isChangeValue,
  });

  return (
    <ReportWallpaper dateTitle={dateTitle}>
      <Comment
        isLoading={isLoading}
        title={titles?.comment_title}
        onTitleChange={handleCommentTitleChange}
        projects={projects}
        value={commentValue}
        onChange={handleCommentChange}
      />
      <UpdatesOfWeek
        isLoading={isLoading}
        title={titles?.changes_title}
        onTitleChange={handleUpdatesOfWeekTitleChange}
        filter={filter}
        projects={projects}
        value={changes}
        onChange={handleUpdatesOfWeekChange}
      />
      <MemoPendingApproval
        ref={memoPendingApprovalInstance}
        isLoading={isLoading}
        title={titles?.memo_title}
        onTitleChange={handleMemoTitleChange}
        filter={filter}
        projects={projects}
        value={memo}
        onChange={handleMemoChange}
      />
      <Calendar
        ref={calendarInstance}
        isLoading={isLoading}
        isInnerLoading={isCalendarInnerLoading}
        setIsInnerLoading={setIsCalendarInnerLoading}
        title={titles?.calendar_title}
        onTitleChange={handleCalendarTitleChange}
        projects={projects}
        onRefreshData={onCalendarRefreshData}
        value={calendar}
        onChange={handleCalendarChange}
      />
    </ReportWallpaper>
  );
};

// @ts-ignore
export default React.forwardRef(ReportForm);
